'use client';
import React, { useState } from 'react';
import { Search, X } from 'lucide-react';
import { POLICIES, INSURERS, formatCurrency, formatPremium } from '@/lib/policyData';

interface PlanSelectorProps {
  onSelect: (id: string) => void;
  onClose: () => void;
  excludeIds: string[];
}

export default function PlanSelector({ onSelect, onClose, excludeIds }: PlanSelectorProps) {
  const [search, setSearch] = useState('');

  const available = POLICIES.filter(
    (p) =>
      !excludeIds.includes(p.id) &&
      (search === '' ||
        p.planName.toLowerCase().includes(search.toLowerCase()) ||
        p.insurerName.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-foreground/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-2xl border border-border shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <h2 className="text-base font-bold text-foreground">Select a Plan</h2>
          <button onClick={onClose} className="p-2 rounded-lg text-muted-foreground hover:bg-muted">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-border flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by plan name or insurer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              className="w-full pl-9 pr-4 py-2 bg-muted border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        {/* Plan list */}
        <div className="overflow-y-auto flex-1 scrollbar-thin p-3 flex flex-col gap-2">
          {available.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No plans found matching &quot;{search}&quot;
            </div>
          ) : (
            available.map((policy) => {
              const ins = INSURERS[policy.insurer];
              return (
                <button
                  key={policy.id}
                  onClick={() => onSelect(policy.id)}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-primary/5 border border-transparent hover:border-primary/20 transition-all duration-150 text-left"
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-extrabold text-xs flex-shrink-0 ${ins.bgColor} ${ins.color}`}>
                    {ins.abbr}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">{ins.name}</p>
                    <p className="text-sm font-semibold text-foreground truncate">{policy.planName}</p>
                    <p className="text-xs text-muted-foreground">{policy.policyType}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-primary font-tabular">{formatPremium(policy.premium)}/yr</p>
                    <p className="text-xs text-muted-foreground">{formatCurrency(policy.coverageAmount)} cover</p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}